import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AMBER, AMBER_DARK, CREAM, INK, MUTED, hexagon, sectionTitle, tile, loadLogo } from './harvestReportPdf';
import { methodFor } from './splitMethods';

const W = 210, M = 14;
const OUTCOME_RGB = { mated_laying: [101, 163, 13], failed: [225, 29, 72], swarmed: [217, 119, 6], combined: [14, 165, 233] };
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
const pct = (v) => (v === null || v === undefined ? '-' : `${v}%`);
const seasonText = (s) => String(s || '').replace('-', ' / ');

const cover = (doc, { businessName, appName, ownerName, apiaryLabel, season, logoDataUrl }) => {
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, W, 62, 'F');
  doc.setDrawColor(251, 191, 36);
  doc.setLineWidth(0.5);
  for (let r = 0; r < 4; r++) for (let c = 0; c < 6; c++) hexagon(doc, 128 + c * 15 + (r % 2) * 7.5, 4 + r * 13, 7.5, 'S');
  doc.setFillColor(...AMBER);
  doc.rect(0, 52, W, 10, 'F');
  let logoW = 0;
  if (logoDataUrl) {
    try {
      const { width, height } = doc.getImageProperties(logoDataUrl);
      const ratio = width / height;
      const lw = ratio >= 1 ? 30 : 18 * ratio, lh = ratio >= 1 ? 30 / ratio : 18;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(M, 12, lw + 4, Math.max(lh + 4, 12), 3, 3, 'F');
      doc.addImage(logoDataUrl, M + 2, 12 + (Math.max(lh + 4, 12) - lh) / 2, lw, lh);
      logoW = lw + 4;
    } catch { /* ignore bad logo */ }
  }
  const tx = logoW ? M + logoW + 5 : M;
  doc.setTextColor(255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text((businessName || appName).toUpperCase(), tx, 17);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.text('Split Season Report', tx, 29);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text(`${ownerName ? `Prepared for ${ownerName}  ·  ` : ''}Generated ${fmtDate(new Date().toISOString())}`, tx, 36);
  let cx = M;
  doc.setFontSize(9);
  [`Apiary: ${apiaryLabel}`, `Season: ${seasonText(season)}`].forEach(c => {
    const cw = doc.getTextWidth(c) + 10;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(cx, 44, cw, 8, 4, 4, 'F');
    doc.setTextColor(...AMBER_DARK);
    doc.setFont('helvetica', 'bold');
    doc.text(c, cx + 5, 49.4);
    cx += cw + 4;
  });
};

// Stacked outcome bar for one summary row
const outcomeBar = (doc, row, outcomes, x, y, w) => {
  doc.setFillColor(245, 245, 244);
  doc.roundedRect(x, y, w, 4.5, 2, 2, 'F');
  let bx = x;
  Object.keys(outcomes).forEach(k => {
    if (!row[k] || !row.recorded) return;
    const bw = (row[k] / row.recorded) * w;
    doc.setFillColor(...OUTCOME_RGB[k]);
    doc.rect(bx, y, bw, 4.5, 'F');
    bx += bw;
  });
};

const summaryTable = (doc, title, rows, outcomes, y) => {
  y = sectionTitle(doc, title, y, 'Success = queen mated & laying, out of splits with a result');
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [['', 'Splits', 'Results', ...Object.values(outcomes), 'Success', '']],
    body: rows.map(r => [r.name, r.total, r.recorded, ...Object.keys(outcomes).map(k => r[k] || 0), pct(r.success_rate), '']),
    styles: { fontSize: 8, cellPadding: 2, textColor: INK },
    headStyles: { fillColor: CREAM, textColor: AMBER_DARK, fontStyle: 'bold', fontSize: 7.5 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 38 }, 7: { halign: 'right', fontStyle: 'bold' }, 8: { cellWidth: 30 } },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 8) {
        outcomeBar(doc, rows[data.row.index], outcomes, data.cell.x + 2, data.cell.y + data.cell.height / 2 - 2.2, data.cell.width - 4);
      }
    },
  });
  return doc.lastAutoTable.finalY + 10;
};

export const buildSplitSeasonReport = ({ report, apiaryLabel, ownerName, businessName, logoDataUrl, appName = 'Thrive Beekeeping' }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const { totals, outcomes } = report;
  cover(doc, { businessName, appName, ownerName, apiaryLabel, season: report.season, logoDataUrl });

  let y = 72;
  const tw = (W - 2 * M - 9) / 4;
  tile(doc, M, y, tw, 26, totals.splits, 'Splits made');
  tile(doc, M + (tw + 3), y, tw, 26, pct(totals.success_rate), 'Success rate', [101, 163, 13]);
  tile(doc, M + 2 * (tw + 3), y, tw, 26, totals.new_hives, 'New colonies', [13, 148, 136]);
  tile(doc, M + 3 * (tw + 3), y, tw, 26, totals.in_progress, 'Still in progress', [202, 138, 4]);
  y += 30;
  tile(doc, M, y, tw, 16, totals.recorded, 'Results recorded', MUTED);
  tile(doc, M + (tw + 3), y, tw, 16, totals.mated_laying, 'Mated & laying', MUTED);
  tile(doc, M + 2 * (tw + 3), y, tw, 16, totals.splits - totals.new_hives, 'Kept in same hive', MUTED);
  tile(doc, M + 3 * (tw + 3), y, tw, 16, totals.splits - totals.recorded, 'Awaiting result', MUTED);
  y += 26;

  if (!report.splits.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...MUTED);
    doc.text('No splits were recorded for this season.', M, y + 6);
  } else {
    y = summaryTable(doc, 'Success by Method', report.by_method, outcomes, y);
    if (apiaryLabel === 'All Apiaries') {
      if (y > 230) { doc.addPage(); y = 20; }
      y = summaryTable(doc, 'Success by Apiary', report.by_apiary, outcomes, y);
    }

    doc.addPage();
    y = sectionTitle(doc, 'Every Split This Season', 20, `${report.splits.length} split${report.splits.length === 1 ? '' : 's'}`);
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [['Date', 'Apiary', 'Method', 'From', 'To', 'Steps', 'Status', 'Result']],
      body: report.splits.map(s => {
        const total = methodFor(s.method_key)?.steps.length;
        return [
          fmtDate(s.start_date), s.yard || '-', s.method, `Hive ${s.parent_hive_number ?? '?'}`,
          s.kept_in_parent ? 'Same hive' : `Hive ${s.destination_hive_number ?? 'new'}`,
          total ? `${s.done_steps}/${total}` : '-',
          s.status === 'in_progress' ? 'In progress' : 'Finished',
          { content: s.outcome_label ? `${s.outcome_label}${s.outcome_date ? `\n${fmtDate(s.outcome_date)}` : ''}` : 'Not recorded',
            styles: { textColor: OUTCOME_RGB[s.outcome] || MUTED, fontStyle: s.outcome ? 'bold' : 'normal' } },
        ];
      }),
      styles: { fontSize: 8, cellPadding: 2, textColor: INK },
      headStyles: { fillColor: AMBER, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: CREAM },
      columnStyles: { 5: { halign: 'center' } },
    });
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(253, 230, 138);
    doc.setLineWidth(0.4);
    doc.line(M, 285, W - M, 285);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(`${businessName || appName} · Split Season Report`, M, 290);
    doc.text(`Page ${i} of ${pages}`, W - M, 290, { align: 'right' });
  }
  return doc;
};

export const downloadSplitSeasonReport = async (opts) => {
  const logoDataUrl = await loadLogo();
  const doc = buildSplitSeasonReport({ ...opts, logoDataUrl });
  const slug = `${opts.apiaryLabel}-${opts.report.season}`.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  doc.save(`split-season-report-${slug}.pdf`);
};
